// Create or edit a cup (contracts/tournaments.md §1, §5, §6, §9; backend/api CupRequest / CupPatchRequest): the
// kind, draft or scheduled, the window in UTC with a local-time preview, the text in 13 languages ({number} is a
// Weekly Cup's number), the banner, the brackets with their level ranges, names and pools, the set size, the prizes
// per bracket and country (gift cards with the cards the winner picks from), for a Championship its qualifying
// and final (async, or live at a set time), and "Hidden from players". Once a cup opens only its text, banner,
// prizes, close time, final and visibility change; a finalized or cancelled cup changes only its visibility (on the
// cup page). What the API accepts but is unusual (a weekly cup by hand, a start in the past) is a warning, never a
// block. The text players read says "reward", never "prize" (the fields' hints say so).

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { type AdminCup, type CupContent, type CupDraft, type CupPatch, useCancelCup, useCreateCup, useCup, useUpdateCup } from "../../api/cups";
import type { ImageRef, Locale } from "../../api/messaging";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, EmptyState, ErrorState, Field, InlineError, Loading, PageHeader, Segmented, TextInput } from "../../components/ui";
import {
  type Bracket,
  CUP_KINDS,
  CUP_LIMITS,
  cupProblems,
  cupWarnings,
  type CupKind,
  DEFAULT_BRACKETS,
  DEFAULT_PRIZES,
  type EditPrize,
  editPrize,
  KIND_LABEL,
  prizeOut,
  REWARD_WORDING_HINT,
  SET_SIZE,
  STATUS_TONE,
} from "../../lib/cups";
import { fmtInt } from "../../lib/format";
import { fromUtcInput, toLocalized, toUtcInput } from "../../lib/messaging";
import { ContentEditor, type ContentState, type FieldSpec, ImageField, textState } from "../messaging/components";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import l from "../learn/learn.module.css";
import { BracketsEditor, CheckRow, cupTitle, PrizesEditor, SchedulePreview } from "./components";
import c from "./cups.module.css";

const TEXT_FIELDS = ["title", "subtitle", "prizeHeadline", "rules"] as const;
type TextKey = (typeof TEXT_FIELDS)[number];
const TEXT_SPECS: readonly FieldSpec<TextKey>[] = [
  { key: "title", label: "Title", limit: CUP_LIMITS.title, placeholder: "e.g. Launch Cup", hint: "{number} is a Weekly Cup's number" },
  { key: "subtitle", label: "Subtitle", limit: CUP_LIMITS.subtitle, rows: 2, placeholder: "e.g. Ten puzzles, one weekend. Play once, any time." },
  { key: "prizeHeadline", label: "Rewards line (shown in the app)", limit: CUP_LIMITS.prizeHeadline, placeholder: "e.g. Top 3: up to a $50 gift card", hint: `on the cup card. ${REWARD_WORDING_HINT}` },
  { key: "rules", label: "Cup rules · optional", limit: CUP_LIMITS.rules, rows: 5, hint: `shown with the Official Rules (iqdaily.app/cups/rules). ${REWARD_WORDING_HINT}` },
];

const HOUR = 3_600_000;
const KIND_SHORT: Record<CupKind, string> = { special: "Special", championship: "Championship", weekly: "Weekly" };
const BANNER = { width: 1200, height: 600 };
/** Tomorrow 10:00 UTC. */
function defaultOpen(): string {
  const d = new Date(Date.now() + 24 * HOUR);
  d.setUTCHours(10, 0, 0, 0);
  return d.toISOString();
}

export function CupEditorPage() {
  const { id } = useParams();
  const cup = useCup(id);
  if (id && cup.isPending) {
    return (
      <Card>
        <Loading rows={8} />
      </Card>
    );
  }
  if (id && cup.isError) {
    return (
      <Card>
        <EmptyState title="No such cup" action={<Link to="/tournaments">← Tournaments</Link>}>
          <ErrorState error={cup.error} onRetry={() => void cup.refetch()} />
        </EmptyState>
      </Card>
    );
  }
  return <CupEditor key={id ?? "new"} cup={id ? (cup.data ?? null) : null} />;
}

/** The body fields the editor compares to find what a PATCH sends. */
type Fields = Omit<CupDraft, "kind" | "status">;

function CupEditor({ cup }: { cup: AdminCup | null }) {
  const canEdit = useCan("cups.edit");
  const { admin } = useAuth();
  const navigate = useNavigate();
  const create = useCreateCup();
  const update = useUpdateCup();
  const cancel = useCancelCup();

  const firstOpen = cup?.opensAt ?? defaultOpen();
  const [kind, setKind] = useState<CupKind>(cup?.kind ?? "special");
  const [asDraft, setAsDraft] = useState(false);
  const [opensAt, setOpensAt] = useState(() => toUtcInput(firstOpen));
  const [closesAt, setClosesAt] = useState(() => toUtcInput(cup?.closesAt ?? new Date(Date.parse(firstOpen) + 48 * HOUR).toISOString()));
  const [text, setText] = useState<ContentState<TextKey>>(() => textState(TEXT_FIELDS, cup?.content));
  const [banner, setBanner] = useState<ImageRef | null>(cup?.bannerImageId && cup.bannerUrl ? { id: cup.bannerImageId, url: cup.bannerUrl, ...BANNER } : null);
  const [brackets, setBrackets] = useState<Bracket[]>(cup?.brackets ?? DEFAULT_BRACKETS);
  const [setSize, setSetSize] = useState(String(cup?.setSize ?? 10));
  const [prizes, setPrizes] = useState<EditPrize[]>(() => (cup ? cup.prizes.map((p) => editPrize({ ...p, giftCards: p.giftCards as EditPrize["giftCards"] })) : DEFAULT_PRIZES.map(editPrize)));
  const [month, setMonth] = useState(cup?.qualifying?.month ?? new Date().toISOString().slice(0, 7));
  const [bestOf, setBestOf] = useState(String(cup?.qualifying?.bestOf ?? 2));
  const [perBracket, setPerBracket] = useState(String(cup?.qualifying?.perBracket ?? 20));
  const [finalMode, setFinalMode] = useState<"async" | "live">(cup?.final?.mode ?? "async");
  const [liveAt, setLiveAt] = useState(() => toUtcInput(cup?.final?.liveAt ?? null));
  const [hidden, setHidden] = useState(cup?.hidden ?? false);
  const [locale, setLocale] = useState<Locale>("en");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const championship = kind === "championship";
  const liveIso = fromUtcInput(liveAt);
  const fields = (): Fields => ({
    opensAt: fromUtcInput(opensAt) ?? "",
    closesAt: fromUtcInput(closesAt) ?? "",
    content: toLocalized(text.draft, TEXT_FIELDS).content as CupContent,
    bannerImageId: banner?.id ?? null,
    brackets: brackets.map((b) => ({ id: b.id, ...(b.name?.trim() ? { name: b.name.trim() } : {}), minLevel: b.minLevel, maxLevel: b.maxLevel ?? null, pool: b.pool })),
    setSize: Number(setSize),
    prizes: prizes.map(prizeOut),
    qualifying: championship ? { month, bestOf: Number(bestOf), perBracket: Number(perBracket) } : null,
    final: championship ? (finalMode === "live" ? { mode: "live", ...(liveIso ? { liveAt: liveIso } : {}) } : { mode: "async" }) : null,
    hidden,
  });
  const current = fields();
  const [baseline, setBaseline] = useState(current);
  const changed = (Object.keys(current) as (keyof Fields)[]).filter((k) => JSON.stringify(current[k]) !== JSON.stringify(baseline[k]));
  const dirty = !cup || changed.length > 0;

  const status = cup?.status ?? "scheduled";
  /** The API's PATCH rules: once open, the schedule, brackets, set size and qualifying are fixed; finalized or cancelled, everything. */
  const frozen = status === "finalized" || status === "cancelled";
  const opened = Boolean(cup) && !frozen && status !== "draft" && status !== "scheduled";
  const form = { kind, opensAt: current.opensAt || null, closesAt: current.closesAt || null, enTitle: text.draft.en.title, brackets, setSize: current.setSize, prizes, qualifying: current.qualifying, final: current.final };
  const problems = cupProblems(form);
  const warnings = cupWarnings(form, { isNew: !cup });
  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const title = cup ? cupTitle(cup) : text.draft.en.title.trim() || "New cup";

  const save = (publish = false) => {
    setNotice(null);
    if (!cup) {
      create.mutate({ kind, ...current, status: asDraft ? "draft" : "scheduled" }, { onSuccess: (made) => navigate(`/tournaments/${made.id}`, { replace: true }) });
      return;
    }
    const patch = Object.fromEntries(changed.map((k) => [k, current[k]])) as CupPatch;
    if (publish) patch.status = "scheduled";
    update.mutate(
      { id: cup.id, patch },
      {
        onSuccess: () => {
          setBaseline(current);
          setNotice(publish ? (hidden ? "Published, and hidden from players." : "Published: players see it in the upcoming cups.") : "Saved.");
        },
      },
    );
  };

  const lockNote = (what: string) => (
    <p className={styles.faint} data-testid="locked-note">
      This cup is {status}: {what}.
    </p>
  );

  return (
    <>
      <PageHeader
        title={title}
        meta={
          <span className={l.statusLine}>
            <Link to={cup ? `/tournaments/${cup.id}` : "/tournaments"} className={l.back}>
              ← {cup ? "Cup" : "Tournaments"}
            </Link>
            <Chip tone={cup ? STATUS_TONE[status] : "info"}>{cup ? status : "new"}</Chip>
            <span>
              {KIND_LABEL[kind]}
              {cup?.number ? ` #${cup.number}` : ""}
            </span>
            {cup && dirty ? <span className={m.required}>unsaved changes</span> : null}
            {!canEdit ? (
              <span className={styles.readOnly} data-testid="read-only">
                Read-only · editing cups needs the editor role (you are {admin?.role})
              </span>
            ) : null}
          </span>
        }
        actions={
          canEdit && !frozen ? (
            <div className={l.headActions}>
              {cup && status !== "closed" ? (
                <Button variant="ghost" onClick={() => setConfirmCancel(true)} disabled={busy} data-testid="cancel-cup">
                  Cancel cup…
                </Button>
              ) : null}
              {cup?.status === "draft" ? (
                <Button variant="secondary" onClick={() => save(true)} busy={busy} disabled={problems.length > 0} title={problems[0]} data-testid="cup-publish">
                  Publish
                </Button>
              ) : null}
              <Button variant="primary" onClick={() => save()} busy={busy} disabled={problems.length > 0 || !dirty} title={problems[0]} data-testid="cup-save">
                {cup ? "Save" : asDraft ? "Create draft" : "Create cup"}
              </Button>
            </div>
          ) : null
        }
      />
      {notice ? (
        <p className={l.savedNotice} data-testid="cup-notice">
          {notice}
        </p>
      ) : null}
      <InlineError error={error} />
      {frozen ? (
        <div className={m.notice} data-tone="info" style={{ marginBottom: 16 }} data-testid="frozen">
          <strong>This cup is {status}: it can't change any more.</strong>
          <span>The API refuses edits to finalized and cancelled cups.</span>
        </div>
      ) : null}

      <div className={l.editor}>
        <div className={l.form}>
          <fieldset className={l.fieldset} disabled={!canEdit || busy || frozen}>
            <Card title="Cup">
              <div className={l.cardBody}>
                {!cup ? (
                  <div className={l.twoUp}>
                    <Field label="Kind" hint={championship ? "Qualifying is the month's Weekly Cups: each player's best scores add up." : kind === "weekly" ? "Numbered after the last Weekly Cup." : "A themed or one-off cup, any window, any prizes."}>
                      <Segmented label="Kind" value={kind} onChange={setKind} options={(["special", "championship", "weekly"] as const satisfies readonly (typeof CUP_KINDS)[number][]).map((k) => ({ value: k, label: KIND_SHORT[k] }))} />
                    </Field>
                    <Field label="Players see it" hint={asDraft ? "A draft is hidden from players until you publish it." : "Listed in the upcoming cups at once."}>
                      <Segmented
                        label="Players see it"
                        value={asDraft ? "draft" : "scheduled"}
                        onChange={(v) => setAsDraft(v === "draft")}
                        options={[
                          { value: "scheduled", label: "Now" },
                          { value: "draft", label: "Not yet (draft)" },
                        ]}
                      />
                    </Field>
                  </div>
                ) : null}
                <CheckRow
                  title="Hidden from players"
                  hint="Not listed in the app, can't be entered and sends no pushes. Its entrants still reach it and its winners can claim. Change it any time, also while it's open."
                  checked={hidden}
                  onChange={setHidden}
                  testId="cup-hidden"
                />
                <div className={l.twoUp}>
                  <Field label="Opens (UTC)">
                    <TextInput type="datetime-local" value={opensAt} disabled={opened} onChange={(e) => setOpensAt(e.target.value)} data-testid="cup-opens" />
                  </Field>
                  <Field label="Closes (UTC)" hint={championship && finalMode === "async" ? "An async final is usually a 24-hour window." : "At most 31 days after it opens."}>
                    <TextInput type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} data-testid="cup-closes" />
                  </Field>
                </div>
                {opened ? lockNote("the opening time, brackets, set size and qualifying can't change any more") : null}
                <SchedulePreview opensAt={current.opensAt || null} closesAt={current.closesAt || null} />
                <Field label="Puzzles per player" hint={`${SET_SIZE.min}–${SET_SIZE.max}, from the bracket's pool: every player gets their own set of matched difficulty.`}>
                  <TextInput type="number" min={SET_SIZE.min} max={SET_SIZE.max} value={setSize} disabled={opened} onChange={(e) => setSetSize(e.target.value)} style={{ width: 120 }} data-testid="cup-set-size" />
                </Field>
                <ImageField image={banner} onChange={setBanner} recommended={BANNER} hint="2:1, on the cup screen; no text in the art" label="Banner · optional" testId="banner" />
              </div>
            </Card>

            {championship ? (
              <Card title="Championship">
                <div className={l.cardBody}>
                  <div className={c.inline}>
                    <Field label="Qualifying month">
                      <TextInput type="month" value={month} disabled={opened} onChange={(e) => setMonth(e.target.value)} data-testid="qual-month" />
                    </Field>
                    <Field label="Best of">
                      <TextInput type="number" min={1} max={5} value={bestOf} disabled={opened} onChange={(e) => setBestOf(e.target.value)} data-testid="qual-best" />
                    </Field>
                    <Field label="Qualify per bracket">
                      <TextInput type="number" min={2} max={100} value={perBracket} disabled={opened} onChange={(e) => setPerBracket(e.target.value)} data-testid="qual-per-bracket" />
                    </Field>
                  </div>
                  <p className={styles.faint}>
                    A player's qualifying score is the sum of their best {bestOf || "N"} Weekly Cup scores among the cups that closed in {month || "the month"} (UTC); the top {perBracket || "N"} of each bracket
                    qualify, in the highest bracket they played. The final opens after the month ends.
                  </p>
                  <Field label="Final" hint={finalMode === "live" ? "An Arena · Cutdown knockout per bracket, for qualifiers only; the lobby opens 15 minutes before." : "Qualifiers play their set any time inside the window."}>
                    <Segmented
                      label="Final"
                      value={finalMode}
                      onChange={setFinalMode}
                      options={[
                        { value: "async", label: "Async (play any time)" },
                        { value: "live", label: "Live (Arena knockout)" },
                      ]}
                    />
                  </Field>
                  {finalMode === "live" ? (
                    <Field label="Live final starts (UTC)" hint="At least 15 minutes after the window opens, and before it closes. Fewer than 2 players show: ranked by qualifying score.">
                      <TextInput type="datetime-local" value={liveAt} onChange={(e) => setLiveAt(e.target.value)} data-testid="final-live-at" />
                    </Field>
                  ) : null}
                  {finalMode === "live" && liveIso ? <SchedulePreview opensAt={liveIso} closesAt={new Date(Date.parse(liveIso) + HOUR / 4).toISOString()} /> : null}
                </div>
              </Card>
            ) : null}

            <Card title="Text">
              <ContentEditor value={text} onChange={setText} fields={TEXT_SPECS} englishRequired={["title"]} locale={locale} onLocale={setLocale} />
            </Card>

            <Card title={`Brackets · ${brackets.length}`}>
              <fieldset className={l.fieldset} disabled={opened}>
                <BracketsEditor value={brackets} onChange={setBrackets} />
              </fieldset>
            </Card>

            <Card title={`Prizes · ${prizes.length}`}>
              <PrizesEditor value={prizes} onChange={setPrizes} brackets={brackets} locale={locale} onLocale={setLocale} />
            </Card>
          </fieldset>
        </div>

        <aside className={l.side}>
          <CupCardPreview text={text} locale={locale} banner={banner} opensAt={current.opensAt} closesAt={current.closesAt} number={cup?.number ?? null} />
          <Card title={problems.length ? "Before it can be saved" : "Ready to save"}>
            {problems.length ? (
              <ul className={l.checklist} data-testid="cup-problems">
                {problems.map((p) => (
                  <li key={p} className={m.required}>
                    {p}
                  </li>
                ))}
              </ul>
            ) : (
              <p className={l.ready} data-testid="cup-ready">
                Every check passes.
                {cup ? (dirty ? ` ${changed.length} ${changed.length === 1 ? "section" : "sections"} changed.` : " No changes.") : asDraft ? " It's created as a draft." : " It's created as scheduled."}
              </p>
            )}
            {warnings.length ? (
              <ul className={l.checklist} data-testid="cup-warnings" style={{ marginTop: problems.length ? 10 : 6 }}>
                {warnings.map((w) => (
                  <li key={w} className={styles.faint}>
                    {w}
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
        </aside>
      </div>

      {cup ? (
        <ConfirmDialog
          open={confirmCancel}
          title={`Cancel “${cupTitle(cup)}”?`}
          confirmLabel="Cancel the cup"
          tone="danger"
          busy={cancel.isPending}
          error={cancel.error}
          onCancel={() => setConfirmCancel(false)}
          onConfirm={() => cancel.mutate(cup.id, { onSuccess: () => navigate(`/tournaments/${cup.id}`) })}
        >
          <p className={styles.prose}>
            {status === "open"
              ? `It's open: its ${fmtInt(cup.entrants)} entrants get a push, and nobody is ranked or wins a prize.`
              : status === "draft"
                ? "It's a draft: players never saw it."
                : "It hasn't opened: it disappears from the app's upcoming cups."}{" "}
            This can't be undone.
          </p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}

/** How the cup card reads in the app, in the language being edited (English where it's empty). */
function CupCardPreview({ text, locale, banner, opensAt, closesAt, number }: { text: ContentState<TextKey>; locale: Locale; banner: ImageRef | null; opensAt: string; closesAt: string; number: number | null }) {
  const pick = (k: TextKey) => (text.draft[locale][k].trim() || text.draft.en[k].trim()).replaceAll("{number}", number === null ? "" : String(number)).trim();
  return (
    <Card title={`Card · ${locale}`}>
      <div className={c.cardPreview} data-testid="cup-card-preview">
        {banner ? <img src={banner.url} alt="" className={c.cardBanner} /> : <div className={c.cardBanner} aria-hidden />}
        <div className={c.cardText}>
          <span className="monoLabel">{opensAt && closesAt ? `${new Date(opensAt).toUTCString().slice(0, 11)} → ${new Date(closesAt).toUTCString().slice(0, 11)}` : "—"}</span>
          <strong>{pick("title") || "Title"}</strong>
          <span>{pick("subtitle") || "Subtitle"}</span>
          {pick("prizeHeadline") ? <em>{pick("prizeHeadline")}</em> : null}
        </div>
      </div>
    </Card>
  );
}
